import { IcoChevronDown16, IcoCopy16, IcoDragAndDrop16 } from '@onefootprint/icons';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { useConfirmationDialog, useToast } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTheme } from 'next-themes';
import { useEffect, useRef, useState } from 'react';
import useSession from '@/hooks/use-session';
import { terminateChatSession } from '../../hooks/use-chat-files';
import { POSITION_ANCHOR } from '../../hooks/use-chat-widget-position';
import useChatWidgetPresentation from '../../hooks/use-chat-widget-presentation';
import type { ChatMessage } from '../../hooks/use-chat-ws';
import useDevtoolsPosition, { type DevtoolsPosition, isDevtoolsCorner, resolveCorner, snapToCorner } from '../../hooks/use-devtools-position';
import useDraggablePosition from '../../hooks/use-draggable-position';
import { resourceKey } from '../../utils/resource-presentation';
type PercyDevtoolsProps = {
  /** The live Modal session, if any. Absent when viewing persisted history */
  session?: ChatSessionResponse;
  messages: ChatMessage[];
  /** Absent for live-session-only views (e.g. a streaming autopilot run) that can't restart. */
  onStartSession?: (prompt: string, files?: File[]) => Promise<void>;
};

// A hue per kind keeps a long log scannable, and the tokens carry six hue families for twelve
// kinds — hence raw hex, as [dark, light]: a hue legible on the dark surface washes out on light.
const TYPE_COLORS: Record<ChatMessage['type'], readonly [string, string]> = {
  user: ['#3b82f6', '#1d4ed8'],
  text: ['#22c55e', '#15803d'],
  thinking: ['#8b5cf6', '#6d28d9'],
  tool_use: ['#f97316', '#c2410c'],
  tool_result: ['#eab308', '#a16207'],
  turn_complete: ['#a855f7', '#7e22ce'],
  task_progress: ['#06b6d4', '#0e7490'],
  error: ['#ef4444', '#b91c1c'],
  system: ['#6b7280', '#4b5563'],
  resource: ['#10b981', '#047857'],
  approval_required: ['#d946ef', '#a21caf'],
  interrupt: ['#9ca3af', '#6b7280']
};

// All message kinds, for the devtools type-filter dropdown.
const MESSAGE_TYPES = Object.keys(TYPE_COLORS) as ChatMessage['type'][];

// The backend stamps every message with its disk-write `timestamp`. Render it as a
// compact local wall-clock time (with millis) for debugging event ordering/latency.
const fmtTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleTimeString(undefined, {
    hour12: false
  })}.${String(d.getMilliseconds()).padStart(3, '0')}`;
};
const summarize = (msg: ChatMessage): string => {
  switch (msg.type) {
    case 'user':
      return msg.content.slice(0, 60);
    case 'text':
      return msg.content.slice(0, 60);
    case 'thinking':
      return msg.content.slice(0, 60);
    case 'tool_use':
      return `${msg.tool.replace('mcp__workspace__', '').replace('mcp__footprint__', '')}: ${(msg.input.command ?? msg.input.url ?? msg.input.path ?? msg.input.file_path ?? msg.input.instruction ?? '') as string}`.slice(0, 40);
    case 'tool_result':
      return `→ ${msg.tool_use_id}`;
    case 'turn_complete':
      return `${msg.num_turns ?? '?'} turns  ${((msg.duration_ms ?? 0) / 1000).toFixed(1)}s`;
    case 'task_progress':
      return msg.data.description.slice(0, 60);
    case 'error':
      return msg.message.slice(0, 60);
    case 'system':
      return msg.content.slice(0, 60);
    case 'resource':
      {
        const ref = resourceKey(msg.resource);
        const verb = msg.kind === 'update' ? 'updated' : 'created';
        const name = 'name' in msg.resource ? msg.resource.name : null;
        return `Resource ${verb} (${msg.resource.kind}): ${name || ref || ''}`;
      }
    case 'approval_required':
      return `${msg.header} (${msg.approval_id})`;
    case 'interrupt':
      return 'interrupt ack';
  }
};

// The explorer panels' header control (see context-explorer.tsx).
const iconButtonClass = 'flex items-center justify-center w-7 h-7 rounded-(--icon-button-border-radius) hover:bg-hover-control cursor-pointer focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary';
const devtoolsButtonClass = 'inline-flex items-center h-7 px-2 rounded-md border border-tertiary text-caption-2 text-secondary hover:bg-hover cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary';

// text-snippet-* is the mono scale (--font-family-code). Don't reach for text-caption-* plus
// font-mono: both declare font-family at the same specificity and caption wins on cascade order.
const fieldClass = 'h-7 rounded-md border border-tertiary bg-secondary px-2 text-snippet-2 text-primary focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary';
const PercyDevtools = ({
  messages,
  onStartSession,
  session
}: PercyDevtoolsProps) => {
  const confirm = useConfirmationDialog();
  const {
    toast
  } = useToast();
  const {
    resolvedTheme
  } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const {
    position,
    setPosition
  } = useDevtoolsPosition();
  const chat = useChatWidgetPresentation();
  // Only the floating card and the collapsed launcher claim a corner. Docked owns the right edge
  // and fullscreen the content area; floating over those is what this panel is for.
  const chatCorner = (chat.presentation === 'floating' || chat.presentation === 'button') && isDevtoolsCorner(chat.position) ? chat.position : null;
  const anchor = resolveCorner(position, chatCorner);
  const isTop = anchor.startsWith('top');
  const isLeft = anchor.endsWith('left');
  // Measure the wrapper, not the header the gesture starts on, so the release settles the whole
  // panel into its corner rather than the title bar.
  const {
    isDragging,
    dragStyle,
    handlers
  } = useDraggablePosition({
    enabled: true,
    onPositionChange: next => setPosition(next as DevtoolsPosition),
    snap: snapToCorner,
    measureRef: wrapperRef
  });
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [isTerminating, setIsTerminating] = useState(false);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ChatMessage['type'] | 'all'>('all');
  const listRef = useRef<HTMLDivElement>(null);
  const {
    data: {
      user
    }
  } = useSession();

  // Filter by kind (dropdown) and free-text. The text match runs against the
  // whole serialized message so it hits content, tool names, ids, paths — not
  // just the row summary.
  const filteredMessages = (() => {
    const q = query.trim().toLowerCase();
    return messages.filter(msg => {
      if (typeFilter !== 'all' && msg.type !== typeFilter) return false;
      if (q && !JSON.stringify(msg).toLowerCase().includes(q)) return false;
      return true;
    });
  })();
  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [open]);
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(filteredMessages, null, 2));
  };
  const toggleRow = (i: number) => {
    setExpandedIndex(prev => prev === i ? null : i);
  };
  const handleTerminate = async () => {
    if (!session) return;
    setIsTerminating(true);
    try {
      await terminateChatSession(session);
      toast({
        title: 'Session terminated',
        description: 'The Percy session has been torn down.'
      });
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Failed to terminate session',
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsTerminating(false);
    }
  };
  const openRestartSessionConfirm = () => {
    confirm.open({
      title: 'Start new chat session?',
      description: 'All Percy conversation context for this chat session will be cleared. The new session will include your initial prompt, but it will not include any files you attached. The new session will start with an empty agent ID and SOP.',
      primaryButton: {
        label: 'Start new session',
        onClick: async () => {
          const firstUserMessage = messages.find(msg_0 => msg_0.type === 'user');
          await onStartSession?.(firstUserMessage?.content ?? '', []);
        }
      },
      secondaryButton: {
        label: 'Cancel'
      }
    });
  };
  if (!user?.isFirmEmployee) return null;
  return <div ref={wrapperRef} className={cx('fixed flex',
  // Top corners grow downward, so the toggle leads and the panel follows.
  isTop ? 'flex-col-reverse' : 'flex-col', isLeft ? 'items-start' : 'items-end', POSITION_ANCHOR[anchor])} style={{
    // Above the chat panels (the design system's `dialog`, 20) so the Context Explorer can't
    // paint over the middle of the log, and below `confirmationOverlay`/`toast` (25/30) so
    // this panel's own confirm dialog and toasts still land on top of it.
    zIndex: 21,
    ['--percy-gap' as string]: '1rem',
    ...dragStyle
  }}>
      {open && <div className={cx('flex flex-col overflow-hidden border border-tertiary bg-canvas shadow-lg',
    // `!` beats the inline size so the panel fits a phone screen.
    'max-md:w-[calc(100vw-32px)]! max-md:h-[min(440px,calc(100dvh-160px))]!', isTop ? 'rounded-b-lg' : 'rounded-t-lg')} style={{
      width: 780,
      height: 440
    }}>
          <div {...handlers} className={cx('flex h-11 shrink-0 items-center gap-1 border-b border-tertiary px-3 select-none touch-none', isDragging ? 'cursor-grabbing' : 'cursor-grab')}>
            <span className="ml-1 shrink-0 text-label-3 font-medium text-primary">Percy Devtools</span>
            <div className="flex flex-1 flex-wrap items-center justify-end gap-1.5">
              {session && <>
                  {session.createSessionFnLogsUrl && <button type="button" onClick={() => window.open(session.createSessionFnLogsUrl, '_blank', 'noopener,noreferrer')} className={devtoolsButtonClass}>
                      Function logs
                    </button>}
                  <button type="button" onClick={() => window.open(session.agentSandboxLogsUrl, '_blank', 'noopener,noreferrer')} className={devtoolsButtonClass}>
                    Agent logs
                  </button>
                  <button type="button" onClick={() => window.open(session.workspaceSandboxLogsUrl, '_blank', 'noopener,noreferrer')} className={devtoolsButtonClass}>
                    Workspace logs
                  </button>
                  {onStartSession && <button type="button" onClick={openRestartSessionConfirm} className={devtoolsButtonClass}>
                      Start new session
                    </button>}
                  <button type="button" onClick={handleTerminate} disabled={isTerminating} className={cx(devtoolsButtonClass, 'text-error')}>
                    {isTerminating ? 'Terminating…' : 'Terminate session'}
                  </button>
                </>}
              <button type="button" aria-label="Copy messages" onClick={handleCopy} className={iconButtonClass}>
                <IcoCopy16 />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 border-b border-tertiary px-3 py-2">
            <input type="text" value={query} onChange={e => {
          setQuery(e.target.value);
          setExpandedIndex(null);
        }} placeholder="Filter messages…" className={cx(fieldClass, 'min-w-0 flex-1')} />
            <span className="whitespace-nowrap text-snippet-3 text-tertiary tabular-nums">
              {filteredMessages.length}/{messages.length}
            </span>
            <select value={typeFilter} onChange={e_0 => {
          setTypeFilter(e_0.target.value as ChatMessage['type'] | 'all');
          setExpandedIndex(null);
        }} className={cx(fieldClass, 'cursor-pointer')}>
              <option value="all">all types</option>
              {MESSAGE_TYPES.map(type => <option key={type} value={type}>
                  {type}
                </option>)}
            </select>
          </div>
          <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto py-1">
            {filteredMessages.length === 0 ? <p className="px-2.5 py-3 text-body-3 text-tertiary">
                {messages.length === 0 ? 'No messages yet.' : 'No messages match the filter.'}
              </p> : filteredMessages.map((msg_1, i_0) => {
          const typeColor = TYPE_COLORS[msg_1.type][isDark ? 0 : 1];
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: append-only devtools log; rows are index-addressed (expand state keyed by i) and messages carry no universal stable id
            <div key={i_0}>
                    <button type="button" onClick={() => toggleRow(i_0)} className={cx('flex w-full cursor-pointer items-center gap-2 px-3 py-1 text-left outline-none transition-colors focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset', expandedIndex === i_0 ? 'bg-senary/60' : 'hover:bg-hover')}>
                      <span className="whitespace-nowrap text-snippet-3 text-tertiary tabular-nums">
                        {fmtTime(msg_1.timestamp)}
                      </span>
                      <span className="shrink-0 whitespace-nowrap rounded-sm px-1.5 text-center text-snippet-2" style={{
                  minWidth: 80,
                  background: `${typeColor}22`,
                  color: typeColor
                }}>
                        {msg_1.type}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-snippet-2 text-tertiary">{summarize(msg_1)}</span>
                      <IcoChevronDown16 className={cx('shrink-0', expandedIndex === i_0 && 'rotate-180')} />
                    </button>
                    {expandedIndex === i_0 && <pre className="m-0 overflow-x-auto whitespace-pre-wrap break-all border-y border-tertiary bg-secondary py-1.5 pl-[18px] pr-2.5 text-snippet-2 text-primary">
                        {JSON.stringify(msg_1, null, 2)}
                      </pre>}
                  </div>
          );
        })}
          </div>
        </div>}
      <button type="button" onClick={() => setOpen(prev_0 => !prev_0)}
    // Same gesture as the title bar, so the collapsed pill is draggable on its own; the hook's
    // onClickCapture swallows the click that ends a drag, so moving it never toggles the panel.
    {...handlers} className={cx('group flex items-center gap-2 touch-none select-none whitespace-nowrap border border-tertiary bg-canvas px-3 py-1 text-caption-2 text-tertiary shadow-lg',
    // Click cursor by default (grab lives on the handle); grabbing while dragging.
    isDragging ? 'cursor-grabbing' : 'cursor-pointer',
    // Square the seam the panel meets while open.
    open ? isTop ? 'rounded-t-md' : 'rounded-b-md' : 'rounded-md')}>
        {/* Drag handle, revealed on hover/drag, matching the chat launcher. Hidden: zero width
            plus -mr-2 cancels the gap so no stray space sits before the label. */}
        <span aria-hidden className={cx('flex shrink-0 items-center justify-center overflow-hidden text-tertiary transition-[width,margin,opacity] duration-150', isDragging ? 'cursor-grabbing' : 'cursor-grab', isDragging ? 'w-4 mr-0 opacity-100' : 'w-0 -mr-2 opacity-0 group-hover:w-4 group-hover:mr-0 group-hover:opacity-100')}>
          <IcoDragAndDrop16 color="tertiary" />
        </span>
        Percy {open ? '▼' : `▲ ${messages.length}`}
      </button>
    </div>;
};
export default PercyDevtools;